import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { iif, of } from 'rxjs';
import { debounceTime, distinct, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { NotificationType } from 'src/app/samwebDescription/samweb/notification/notificationType';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { SupervisingService } from 'src/generated/swagger';
import { CallWaitListBehaviourState } from '../dashboard/brick-wall/brick-behaviour-state/brickTypes/call-wait-list-behaviour/call-wait-list-behaviour.state';
import { DirectoryHelperState } from '../dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-helper/directory-helper.state';
import { ChatListState } from '../dashboard/brick-wall/chat-list/chat-list.state';
import { DirectoryEditingListState } from '../dashboard/directory-editing-list/directory-editing-list.state';
import { EmailListDialogState } from '../dashboard/email-list/email-list-dialog.state';
import { ToastUserInfoNotification } from '../notification/notification-toastr/notification-toastr.actions';
import { EnabledFeaturesState } from '../session/enabled-features/enabled-features.state';
import { SamwebWindowState } from '../utility/samweb-window/samweb-window.state';
import { AppointmentDialogState } from './appointment-dialog/appointment-dialog.state';
import { CallTagsSelectors } from './call-tags/call-tags.selectors';
import { CallTagsState } from './call-tags/call-tags.state';
import { ChangeOwnPasswordState } from './change-password/change-own-password.state';
import { ChatsState } from './chats/chats.state';
import { ClientPhoneState } from './client-phone/client-phone.state';
import { ContactStatesState } from './contact-states/contact-states.state';
import { CurrentDialTargetState } from './current-dial-target/current-dial-target.state';
import { DirectoryState } from './directory/directory.state';
import { SelectedSubscriberState } from './directory/selected-subscriber/selected-subscriber.state';
import { EmailBehaviourState } from './email/email-behaviour/email-behaviour.state';
import { EmailDialogBehaviourState } from './email/email-dialog/email-dialog-behaviour.state';
import { EmailTemplateState } from './email/email-template/email-template.state';
import { OpenDynamicLinkState } from './open-dynamic-link/open-dynamic-link.state';
import { QueueSelectionDialogState } from './queue-selection-dialog/queue-selection-dialog.state';
import { SamwinAcdExtensionState } from './samwin-acd-extension/samwin-acd-extension.state';
import { SamwinAgentListState } from './samwin-agent-list/samwin-agent-list.state';
import { SamwinCallHistoryState } from './samwin-call-history/samwin-call-history.state';
import { SamwinCallListState } from './samwin-call-list/samwin-call-list.state';
import { SamwinLinesStateSelector } from './samwin-lines/samwin-lines.selector';
import { SamwinLinesState } from './samwin-lines/samwin-lines.state';
import { SamwinMessageListStateSelectors } from './samwin-mail-list/samwin-message-list.selectors';
import { SamwinMessageListState } from './samwin-mail-list/samwin-message-list.state';
import { SamwinMessageSkillBrowserState } from './samwin-mail-skill-browser/samwin-mail-skill-browser.state';
import { SamwinMaliciousCallMailConfigState } from './samwin-malicious-call-mail-config/samwin-malicious-call-mail-config.state';
import { SamwinMessageLinesState } from './samwin-message-lines/samwin-message-lines.state';
import { SamwinOutboundSkillShortInfoState } from './samwin-outbound-skill-shortinfo/samwin-outbound-skill-shortinfo.state';
import { SamwinSkillsState } from './samwin-skills/samwin-skills.state';
import { SamwinUserAssistanceState } from './samwin-user-assistance-status/samwin-user-assistance-status.state';
import { SamwinUserConfigurationState } from './samwin-user-configuration/samwin-user-configuration.state';
import { SamwinUserStatusState } from './samwin-user-status/samwin-user-status.state';
import { OpenUrlInNewWindow } from './samwin.actions';
import { ShortcutBehaviourState } from './shortcut-behaviour/shortcut-behaviour.state';
import { SupervisingChangeSkillStatus, SupervisingRequestUserStatusChange } from './supervising.actions';
import { TransferState } from './transfer/transfer.state';
import { UserGroupsState } from './user-groups/user-groups.state';

// tslint:disable-next-line:no-empty-interface
export interface SamwinStateModel {}

@State<SamwinStateModel>({
   name: 'samwin',
   defaults: {},
   children: [
      UserGroupsState,
      TransferState,
      SamwinMessageSkillBrowserState,
      SamwinMessageListStateSelectors,
      SamwinMessageListState,
      SamwinMessageLinesState,
      EmailDialogBehaviourState,
      EmailBehaviourState,
      EmailListDialogState,
      DirectoryEditingListState,
      ChatListState,
      AppointmentDialogState,
      ShortcutBehaviourState,
      SelectedSubscriberState,
      SamwinUserStatusState,
      SamwinUserConfigurationState,
      SamwinUserAssistanceState,
      SamwinSkillsState,
      SamwinOutboundSkillShortInfoState,
      SamwinMessageSkillBrowserState,
      SamwinMessageLinesState,
      SamwinMessageListStateSelectors,
      SamwinMessageListState,
      SamwinMaliciousCallMailConfigState,
      SamwinLinesState,
      SamwinCallListState,
      SamwinCallHistoryState,
      SamwinAgentListState,
      SamwinAcdExtensionState,
      SamwebWindowState,
      QueueSelectionDialogState,
      OpenDynamicLinkState,
      EmailListDialogState,
      EmailDialogBehaviourState,
      EmailBehaviourState,
      DirectoryState,
      DirectoryEditingListState,
      DirectoryHelperState,
      CurrentDialTargetState,
      ContactStatesState,
      ClientPhoneState,
      ChangeOwnPasswordState,
      CallWaitListBehaviourState,
      CallTagsState,
      AppointmentDialogState,
      ChatsState,
      EmailTemplateState,
   ],
})
@Injectable()
export class SamwinState implements NgxsOnInit {
   constructor(private store: Store, private supervisingService: SupervisingService) {}

   public ngxsOnInit() {
      this.store
         .select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.DO_OPEN_CI_URLS_OF_SKILLS_IN_NEW_WINDOW))
         .pipe(
            switchMap((isEnabled) =>
               iif(
                  () => isEnabled,
                  this.store.select(SamwinLinesState.getActiveOrRingingCallId).pipe(
                     distinctUntilChanged(),
                     switchMap(() =>
                        this.store.select(SamwinLinesStateSelector.getSourceLineState).pipe(
                           distinct(),
                           switchMap(() =>
                              this.store.select(ClientPhoneState.getCurrentSkillUrl).pipe(
                                 // a null URL indicates that currently not all information is available
                                 // e.g. after logon -> continue until we have a valid (or empty) URL
                                 filter((skillUrl) => !_.isNil(skillUrl)),
                                 debounceTime(250)
                              )
                           )
                        )
                     ),
                     filter((skillUrl) => !_.isEmpty(skillUrl)),
                     filter((skillUrl) => skillUrl.toLowerCase() !== 'about:blank'),
                     tap((skillUrl) => this.store.dispatch(new OpenUrlInNewWindow(skillUrl)))
                  ),
                  of(null)
               )
            )
         )
         .subscribe();
   }

   @Action(OpenUrlInNewWindow)
   public openUrlInNewWindow(ctx: StateContext<SamwinStateModel>, action: OpenUrlInNewWindow) {
      // replace tags in URL
      const resolvedUrl = _.trim(
         this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn)(action.urlWithWildCards).text
      );
      if (_.isEmpty(resolvedUrl)) {
         return;
      }

      const newWindow = window.open(resolvedUrl);
      if (_.isNil(newWindow)) {
         return this.store.dispatch(
            new ToastUserInfoNotification(
               NotificationType.WARN,
               'notification.open_url_in_new_window_failed.title',
               'notification.open_url_in_new_window_failed.message'
            )
         );
      }
      // set focus on newly opened window
      newWindow.focus();
   }

   @Action(SupervisingRequestUserStatusChange)
   public supervisingRequestUserStatusChange(ctx: StateContext<SamwinStateModel>, action: SupervisingRequestUserStatusChange) {
      return this.supervisingService.requestUserStatusChange({
         onBehalfOf: action.onBehalfOf,
         status: action.status,
      });
   }

   @Action(SupervisingChangeSkillStatus)
   public supervisingChangeSkillStatus(ctx: StateContext<SamwinStateModel>, action: SupervisingChangeSkillStatus) {
      return this.supervisingService.changeSkillStatus({
         onBehalfOf: action.onBehalfOf,
         skillId: action.skillId,
         skillShouldBeActive: action.shouldSkillBeActive,
      });
   }
}
